import './SignUp.scss';
import './Terms.scss';
import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Button from '@mui/material/Button';
import fnc from '../mymod/commFunction';
import TermsOfService from './TermsOfService';
import PrivacyPolicy from './PrivacyPolicy';

function SignUp() {
  const navigate = useNavigate();
  
  // 입력 필드 상태
  const [userId, setUserId] = useState('');
  const [password, setPassword] = useState('');
  const [passwordConfirm, setPasswordConfirm] = useState('');
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [certCode, setCertCode] = useState('');
  const [couponCode, setCouponCode] = useState('');
  
  // SMS 인증 상태
  const [sentCode, setSentCode] = useState('');
  const [sentTime, setSentTime] = useState(null);
  const [phoneVerified, setPhoneVerified] = useState(true); // 테스트용: 인증 skip
  const [sendCount, setSendCount] = useState(0);
  const [isSending, setIsSending] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  
  // 약관 동의 상태
  const [agreeTerms, setAgreeTerms] = useState(false);
  const [agreePrivacy, setAgreePrivacy] = useState(false);
  const [agreeMarketing, setAgreeMarketing] = useState(false);
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [showPrivacyModal, setShowPrivacyModal] = useState(false);
  
  // 쿨다운 타이머
  const cooldownTimerRef = useRef(null);

  // 아이디 유효성 검증
  const validateUserId = () => {
    if (!userId.trim()) {
      alert('아이디를 입력해주세요.');
      return false;
    }
    if (!/^[a-zA-Z0-9]{4,20}$/.test(userId)) {
      alert('아이디는 영문, 숫자 조합으로 4-20자여야 합니다.');
      return false;
    }
    return true;
  };

  // 비밀번호 유효성 검증
  const validatePassword = () => {
    if (!password) {
      alert('비밀번호를 입력해주세요.');
      return false;
    }
    if (password.length < 8) {
      alert('비밀번호는 8자 이상이어야 합니다.');
      return false;
    }
    if (!passwordConfirm) {
      alert('비밀번호 확인을 입력해주세요.');
      return false;
    }
    if (password !== passwordConfirm) {
      alert('비밀번호가 일치하지 않습니다.');
      return false;
    }
    return true;
  };

  // 핸드폰 번호 유효성 검증
  const validatePhone = () => {
    if (!phone.trim()) {
      alert('핸드폰 번호를 입력해주세요.');
      return false;
    }
    if (!/^01[0-9]{8,9}$/.test(phone)) {
      alert('올바른 핸드폰 번호를 입력해주세요.\n(예: 01012345678)');
      return false;
    }
    return true;
  };

  // 이메일 유효성 검증
  const validateEmail = () => {
    if (!email.trim()) {
      alert('이메일을 입력해주세요.');
      return false;
    }
    if (!/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(email)) {
      alert('올바른 이메일 형식을 입력해주세요.');
      return false;
    }
    return true;
  };

  // 이름 유효성 검증
  const validateName = () => {
    if (!name.trim()) {
      alert('이름을 입력해주세요.');
      return false;
    }
    if (name.length > 20) {
      alert('이름은 20자 이내로 입력해주세요.');
      return false;
    }
    return true;
  };

  // 핸드폰 번호 변경 시 인증 초기화
  const handlePhoneChange = (e) => {
    setPhone(e.target.value);
    setSentCode('');
    setSentTime(null);
    setPhoneVerified(false);
    setCertCode('');
  };

  // 인증번호 전송
  const handleSendCertCode = () => {
    // 기본 검증
    if (!validateUserId() || !validatePassword() || !validatePhone() || !validateEmail()) {
      return;
    }

    // 최대 전송 횟수 체크
    if (sendCount >= 5) {
      alert('인증번호 전송 횟수를 초과했습니다 (최대 5회).\n페이지를 새로고침하고 다시 시도해주세요.');
      return;
    }

    // 쿨다운 체크 (60초)
    if (sentTime) {
      const elapsed = (Date.now() - sentTime) / 1000;
      if (elapsed < 60) {
        const remaining = Math.ceil(60 - elapsed);
        alert(`인증번호를 너무 자주 요청했습니다.\n${remaining}초 후에 다시 시도해주세요.`);
        return;
      }
    }

    setIsSending(true);
    
    fnc.sendAxios('/signupRoutes/send-verification-sms', 
      { phone, userId }, 
      (res) => {
        setIsSending(false);
        if (res.success) {
          setSentCode(res.code);
          setSentTime(Date.now());
          setSendCount(prev => prev + 1);
          setPhoneVerified(false);
          setCertCode('');
          alert('인증번호가 전송되었습니다.\n5분 안에 입력해주세요.');
        } else {
          alert(res.message || '인증번호 전송에 실패했습니다.');
        }
      }
    );
  };

  // 인증번호 확인
  const handleVerifyCertCode = () => {
    if (!sentCode || !sentTime) {
      alert('먼저 인증번호를 전송해주세요.');
      return;
    }

    // 5분 만료 체크
    const elapsed = (Date.now() - sentTime) / 1000;
    if (elapsed > 300) {
      alert('인증번호가 만료되었습니다 (5분 초과).\n인증번호를 다시 전송해주세요.');
      setSentCode('');
      setPhoneVerified(false);
      setCertCode('');
      return;
    }

    const inputCode = certCode.trim();
    if (inputCode === sentCode) {
      setPhoneVerified(true);
      alert('핸드폰 인증이 완료되었습니다.');
    } else {
      setPhoneVerified(false);
      alert('인증번호가 일치하지 않습니다.');
    }
  };

  // 회원가입 처리
  const handleSubmit = (e) => {
    e.preventDefault();

    // 유효성 검증
    if (!validateUserId() || !validatePassword() || !validatePhone() || 
        !validateEmail() || !validateName()) {
      return;
    }

    // 핸드폰 인증 체크 (테스트용 주석 처리)
    // if (!phoneVerified) {
    //   alert('핸드폰 인증이 필요합니다.\n인증번호를 전송하고 인증을 완료해주세요.');
    //   return;
    // }

    // 약관 동의 체크
    if (!agreeTerms) {
      alert('이용약관에 동의해주세요.');
      return;
    }
    if (!agreePrivacy) {
      alert('개인정보 처리방침에 동의해주세요.');
      return;
    }

    // 가입 확인
    if (!window.confirm(
      `아래 정보로 회원가입을 진행하시겠습니까?\n\n` +
      `  아이디 : ${userId}\n` +
      `  이름   : ${name}\n` +
      `  핸드폰 : ${phone}\n` +
      `  이메일 : ${email}`
    )) {
      return;
    }

    // 회원가입 요청
    fnc.sendAxios('/signupRoutes/register', 
      {
        userId,
        password,
        email,
        name,
        phone,
        couponCode: couponCode.trim(),
        agreeTerms,
        agreePrivacy,
        agreeMarketing
      },
      (res) => {
        if (res.success) {
          let message = '회원가입이 완료되었습니다!';
          if (res.couponApplied) {
            message += `\n\n쿠폰 적용: ${couponCode}\n${res.couponMessage}`;
          } else if (res.couponMessage) {
            message += `\n\n쿠폰 적용 실패: ${res.couponMessage}`;
          }
          alert(message);
          navigate('/login');
        } else {
          alert(res.message || '회원가입에 실패했습니다.');
        }
      }
    );
  };

  return (
    <div className='SignUp'>
      <form onSubmit={handleSubmit}>
        <p className='title'>회원가입</p>
        <p className='signup-install-notice'>설치파일을 다운로드 받아 설치 후 로그인 하셔야 합니다.</p>
        
        <div>
          <label htmlFor='userId'>*아이디</label>
          <input
            id='userId'
            type='text'
            value={userId}
            onChange={(e) => setUserId(e.target.value)}
            placeholder='영문, 숫자 4-20자'
          />
        </div>

        <div>
          <label htmlFor='password'>*비밀번호</label>
          <input
            id='password'
            type='password'
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder='8자 이상'
          />
        </div>

        <div>
          <label htmlFor='passwordConfirm'>*비밀번호 확인</label>
          <input
            id='passwordConfirm'
            type='password'
            value={passwordConfirm}
            onChange={(e) => setPasswordConfirm(e.target.value)}
            placeholder='비밀번호 재입력'
          />
        </div>

        <div>
          <label htmlFor='email'>*이메일</label>
          <input
            id='email'
            type='email'
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder='example@email.com'
          />
        </div>

        <div>
          <label htmlFor='name'>*이름</label>
          <input
            id='name'
            type='text'
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder='이름 입력'
          />
        </div>

        {/* 테스트용 주석 처리 - 핸드폰 인증 skip
        <div>
          <label htmlFor='phone'>*핸드폰</label>
          <div className='input-with-button'>
            <input
              id='phone'
              type='text'
              value={phone}
              onChange={handlePhoneChange}
              placeholder='01012345678'
              className='phone-input'
            />
            <Button
              variant='contained'
              className='cert-send-btn'
              onClick={handleSendCertCode}
              disabled={isSending}
            >
              {isSending ? '전송중...' : '인증전송'}
            </Button>
          </div>
        </div>

        <div>
          <label htmlFor='certCode'>*인증번호</label>
          <div className='input-with-button'>
            <input
              id='certCode'
              type='text'
              value={certCode}
              onChange={(e) => setCertCode(e.target.value)}
              placeholder='인증번호 6자리'
              disabled={!sentCode}
              className={`cert-input ${phoneVerified ? 'verified' : ''}`}
            />
            <Button
              variant='contained'
              className='cert-verify-btn'
              onClick={handleVerifyCertCode}
              disabled={!sentCode || isVerifying}
            >
              확인
            </Button>
          </div>
        </div>
        */}
        <div>
          <label htmlFor='phone'>*핸드폰</label>
          <input
            id='phone'
            type='text'
            value={phone}
            onChange={handlePhoneChange}
            placeholder='01012345678'
          />
        </div>

        <div>
          <label htmlFor='couponCode'>쿠폰코드 (선택사항)</label>
          <input
            id='couponCode'
            type='text'
            value={couponCode}
            onChange={(e) => setCouponCode(e.target.value)}
            placeholder='쿠폰코드 입력'
          />
        </div>

        {/* 약관 동의 */}
        <div className='terms-agreement'>
          <div className='checkbox-row'>
            <input
              type='checkbox'
              id='agreeTerms'
              checked={agreeTerms}
              onChange={(e) => setAgreeTerms(e.target.checked)}
            />
            <label htmlFor='agreeTerms'>
              <span className='required'>[필수]</span> 이용약관 동의
              <span className='view-link' onClick={() => setShowTermsModal(true)}>
                보기
              </span>
            </label>
          </div>

          <div className='checkbox-row'>
            <input
              type='checkbox'
              id='agreePrivacy'
              checked={agreePrivacy}
              onChange={(e) => setAgreePrivacy(e.target.checked)}
            />
            <label htmlFor='agreePrivacy'>
              <span className='required'>[필수]</span> 개인정보 처리방침 동의
              <span className='view-link' onClick={() => setShowPrivacyModal(true)}>
                보기
              </span>
            </label>
          </div>

          <div className='checkbox-row'>
            <input
              type='checkbox'
              id='agreeMarketing'
              checked={agreeMarketing}
              onChange={(e) => setAgreeMarketing(e.target.checked)}
            />
            <label htmlFor='agreeMarketing'>
              [선택] 마케팅 정보 수신 동의 (이벤트, 할인 정보 등)
            </label>
          </div>

          <div className='checkbox-row all-agree'>
            <input
              type='checkbox'
              id='agreeAll'
              checked={agreeTerms && agreePrivacy && agreeMarketing}
              onChange={(e) => {
                const checked = e.target.checked;
                setAgreeTerms(checked);
                setAgreePrivacy(checked);
                setAgreeMarketing(checked);
              }}
            />
            <label htmlFor='agreeAll'>
              전체 동의
            </label>
          </div>
        </div>

        <div className='button-group'>
          <Button variant='contained' className='btnSignUp' type='submit'>
            회원가입
          </Button>
          <Button
            variant='outlined'
            className='btnCancel'
            type='button'
            onClick={() => navigate('/login')}
          >
            취소
          </Button>
        </div>
      </form>

      {/* 이용약관 모달 */}
      {showTermsModal && (
        <div className='terms-modal' onClick={() => setShowTermsModal(false)}>
          <div className='modal-content' onClick={(e) => e.stopPropagation()}>
            <div className='modal-header'>
              <h3>이용약관</h3>
              <button className='close-btn' onClick={() => setShowTermsModal(false)}>×</button>
            </div>
            <div className='modal-body'>
              <TermsOfService />
            </div>
            <div className='modal-footer'>
              <button onClick={() => setShowTermsModal(false)}>닫기</button>
            </div>
          </div>
        </div>
      )}

      {/* 개인정보 처리방침 모달 */}
      {showPrivacyModal && (
        <div className='terms-modal' onClick={() => setShowPrivacyModal(false)}>
          <div className='modal-content' onClick={(e) => e.stopPropagation()}>
            <div className='modal-header'>
              <h3>개인정보 처리방침</h3>
              <button className='close-btn' onClick={() => setShowPrivacyModal(false)}>×</button>
            </div>
            <div className='modal-body'>
              <PrivacyPolicy />
            </div>
            <div className='modal-footer'>
              <button onClick={() => setShowPrivacyModal(false)}>닫기</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default SignUp;
